import React from "react";
import t from "../../../../i18n";
import { colors, fonts, spacings } from "@ui/Theme";
import InputV2 from "@ui/Input/InputV2";
import { styled } from "@linaria/react";
import { fractionalToSeconds } from "../../../../lib/time/seconds";
import ErrorWarningLine from "@ui/IconV2/react/ErrorWarningLine";
import { css } from "@linaria/core";

interface Props {
  onChange: (durationInS: number) => void;
  error?: string;
  className?: string;
}

const toPositiveIntegerOrNull = (value: string) => {
  if (value.trim() === "") {
    return null;
  }
  const number = parseInt(value, 10);
  return isNaN(number) ? null : Math.max(0, number);
};

export const DurationInput = ({ onChange, error, className }: Props) => {
  const [hours, setHours] = React.useState<number | null>(null);
  const [minutes, setMinutes] = React.useState<number | null>(null);
  const [days, setDays] = React.useState<number | null>(null);

  React.useEffect(() => {
    const durationInS = fractionalToSeconds({
      hours: hours ?? 0,
      minutes: minutes ?? 0,
      days: days ?? 0,
      seconds: 0,
    });
    onChange(durationInS);
  }, [days, hours, minutes, onChange]);

  return (
    <div className={className}>
      <FieldRow>
        <InputV2
          type="number"
          style={error ? errorStyle : undefined}
          label={t("measures.chart.selected.analyses.modal.activate.form.labels.days")}
          step="1"
          min={0}
          onChange={e => {
            setDays(toPositiveIntegerOrNull(e.target.value));
          }}
          value={days ?? ""}
        />
        <InputV2
          type="number"
          style={error ? errorStyle : undefined}
          label={t("measures.chart.selected.analyses.modal.activate.form.labels.hours")}
          onChange={e => {
            setHours(toPositiveIntegerOrNull(e.target.value));
          }}
          min={0}
          value={hours ?? ""}
        />
        <InputV2
          type="number"
          style={error ? errorStyle : undefined}
          label={t("measures.chart.selected.analyses.modal.activate.form.labels.minutes")}
          onChange={e => {
            setMinutes(toPositiveIntegerOrNull(e.target.value));
          }}
          min={0}
          value={minutes ?? ""}
        />
      </FieldRow>
      {error && (
        <p className={fieldErrorCss}>
          <ErrorWarningLine size="s" />
          <span>{error}</span>
        </p>
      )}
    </div>
  );
};

const errorStyle = {
  color: `${colors.carbon900}`,
  borderColor: `${colors.oxygen400}`,
};

const FieldRow = styled.div`
  display: flex;
  gap: ${spacings.s};
`;

const field = css`
  column-gap: ${spacings.xxs};
  display: inline-grid;
  grid-template-columns: min-content 1fr;
  grid-template-rows: min-content;
  grid-auto-columns: min-content;
  grid-auto-rows: min-content;
  font-family: "Open Sans", sans-serif;
  row-gap: ${spacings.xxs};
`;

const fieldHint = css`
  color: ${colors.carbon700};
  grid-column: 1 / span 2;

  .${field}.disabled & {
    opacity: 0.5;
  }
`;
const fieldErrorCss = css`
  color: ${colors.oxygen400};
  font: ${fonts.body2};
  margin-top: ${spacings.xxs};
  grid-column: 1 / span 2;
  grid-row: 3 / span 1;
  text-align: left;
  display: flex;
  gap: 4px;
  align-items: flex-start;

  .${fieldHint} ~ & {
    grid-row: 4 / span 1;
  }

  > :first-child {
    flex-shrink: 0;
    height: 18px;
  }
`;
